import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Skeleton,
  Typography,
  useTheme,
} from '@mui/material';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CurrencyExchangeOutlinedIcon from '@mui/icons-material/CurrencyExchangeOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import { getBnplMovements } from '../../queries/bnpl';
import type { BnplMovementListItem } from '../../types/bnpl';
import { getBnplMovementsStyles } from './style';
import AlertDialog from '../AlertDialog';

const movementSubTypesParse = {
  INSTALLMENT_PAYMENT: 'Pago de cuota',
  PURCHASE: 'Nueva compra',
  PREAPPROVE_UPDATE: 'Nuevo monto pre-aprobado',
  CONTRACT_UPDATE: 'Firma del contrato',
  INSTALLMENT_CANCELED: 'Cuota cancelada',
  REVERSE: 'Cobro reverso',
  INSTALLMENT_REACTIVATION: 'Reactivación de cuota',
  INSTALLMENT_REFINANCE: 'Refinanciamiento de cuota',
};

function formatClpPlain(value: number): string {
  return new Intl.NumberFormat('es-CL', {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(value);
}

function formatShortDateEs(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d
    .toLocaleDateString('es-CL', { day: 'numeric', month: 'short' })
    .replace(/\./g, '')
    .toLowerCase();
}

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) % 1_000_000_007;
  }
  return h;
}

function merchantTitle(item: BnplMovementListItem): string {
  return movementSubTypesParse[item.subType as keyof typeof movementSubTypesParse] || item.type;
}

function formatMovementAmount(amount: number): { text: string; isDebit: boolean } {
  const abs = Math.abs(amount);
  const formatted = formatClpPlain(abs);
  if (amount < 0) {
    return { text: `-$ ${formatted}`, isDebit: true };
  }
  if (amount > 0) {
    return { text: `+$ ${formatted}`, isDebit: false };
  }
  return { text: `$ ${formatted}`, isDebit: false };
}

type MovementRowProps = {
  item: BnplMovementListItem;
  sx: ReturnType<typeof getBnplMovementsStyles>;
};

const movementRowIconSx = { fontSize: 22, display: 'block' as const };

function MovementRowIcon({ subType }: { subType: string }) {
  switch (subType) {
    case 'INSTALLMENT_PAYMENT':
      return <PaymentsOutlinedIcon sx={movementRowIconSx} aria-hidden />;
    case 'PURCHASE':
      return <ShoppingBagOutlinedIcon sx={movementRowIconSx} aria-hidden />;
    case 'PREAPPROVE_UPDATE':
      return <TrendingUpOutlinedIcon sx={movementRowIconSx} aria-hidden />;
    case 'CONTRACT_UPDATE':
      return <AssignmentTurnedInOutlinedIcon sx={movementRowIconSx} aria-hidden />;
    case 'INSTALLMENT_CANCELED':
      return <CancelOutlinedIcon sx={movementRowIconSx} aria-hidden />;
    case 'REVERSE':
      return <UndoOutlinedIcon sx={movementRowIconSx} aria-hidden />;
    case 'INSTALLMENT_REACTIVATION':
      return <AutorenewOutlinedIcon sx={movementRowIconSx} aria-hidden />;
    case 'INSTALLMENT_REFINANCE':
      return <CurrencyExchangeOutlinedIcon sx={movementRowIconSx} aria-hidden />;
    default:
      return <ReceiptLongOutlinedIcon sx={movementRowIconSx} aria-hidden />;
  }
}

function MovementRow({ item, sx }: MovementRowProps) {
  const title = merchantTitle(item);
  const useTintAvatar = hashString(item.id) % 2 === 0;
  const amountDisplay = formatMovementAmount(item.amount);

  if (item.type === 'NON_FINANCIAL') return null;

  return (
    <Box component="li" sx={sx.row}>
      <Box sx={sx.merchantRow}>
        <Box
          component="span"
          aria-hidden
          sx={{
            ...sx.avatar,
            ...(useTintAvatar ? sx.avatarTint : sx.avatarMuted),
            fontSize: undefined,
          }}
        >
          <MovementRowIcon subType={item.subType} />
        </Box>
        <Typography component="div" variant="caption" fontWeight="bold" sx={sx.merchantName}>
          {title}
        </Typography>
      </Box>

      <Box sx={sx.detailRow}>
        <Box sx={sx.detailLeft}>
          {item.type === 'NON_FINANCIAL' && (
            <Typography
              component="p"
              variant="caption"
              fontWeight="bold"
              sx={sx.datailNonFinancial}
            >
              Nuevo saldo: $
              {formatClpPlain(item.balanceAfterMovement)}
            </Typography>
          )}
          <Typography
            component="time"
            variant="caption"
            fontWeight="bold"
            dateTime={item.createdAt}
            sx={{ ...sx.dateShort, ...(item.type === 'NON_FINANCIAL' && { marginTop: 0 }) }}
          >
            {formatShortDateEs(item.createdAt)}
          </Typography>
        </Box>
        <Box sx={sx.detailRight}>
          <Typography
            component="div"
            variant="body2"
            sx={{
              ...sx.amountMovement,
              ...(!amountDisplay.isDebit ? sx.amountMovementCredit : {}),
            }}
          >
            {amountDisplay.text}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export type BnplMovementsProps = {
  token: string | null;
  accordion?: boolean;
  defaultExpanded?: boolean;
};

function BnplMovements({ token, accordion, defaultExpanded }: BnplMovementsProps) {
  const theme = useTheme();
  const sx = getBnplMovementsStyles(theme);

  const {
    movementItems,
    movementsLoading,
    movementsLoadingMore,
    movementsError,
    movementsHasMore,
    movementsLoadMore,
    movementsRefresh,
  } = getBnplMovements(token, 'FINANCIAL');

  if (!token) {
    return null;
  }

  const listContent = (
    <>
      {movementsLoading && (defaultExpanded || !accordion) && (
        <Box sx={{ py: 1 }}>
          <Skeleton variant="rectangular" height={96} sx={{ mb: 1.5 }} />
          <Skeleton variant="rectangular" height={96} sx={{ mb: 1.5 }} />
          <Skeleton variant="rectangular" height={96} />
        </Box>
      )}

      {!movementsLoading && movementsError && (
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            No pudimos cargar tus movimientos. Intenta de nuevo.
          </Typography>
          <Button size="small" variant="outlined" color="primary" onClick={() => movementsRefresh()}>
            Reintentar
          </Button>
        </Box>
      )}

      {!movementsLoading && !movementsError && movementItems.length === 0 && (
        <Typography variant="body2" sx={sx.emptyText}>
          Aún no registras movimientos con
          {' '}
          <Typography variant="body2" color="secondary" component="span">banca.me</Typography>
          .
        </Typography>
      )}

      {!movementsLoading && !movementsError && movementItems.length > 0 && (
      <Box
        component="ul"
        sx={{
          ...sx.list,
          listStyle: 'none',
          m: 0,
          p: 0,
        }}
      >
        {movementItems.map((item) => (
          <MovementRow key={item.id} item={item} sx={sx} />
        ))}
      </Box>
      )}

      {!movementsLoading && !movementsError && movementsHasMore && (
        <Box sx={sx.loadMoreWrap}>
          <Button
            size="small"
            variant="text"
            color="primary"
            disabled={movementsLoadingMore}
            onClick={() => movementsLoadMore()}
            startIcon={
              movementsLoadingMore
                ? <CircularProgress size={16} color="inherit" />
                : undefined
            }
          >
            {movementsLoadingMore ? 'Cargando…' : 'Cargar más'}
          </Button>
        </Box>
      )}
    </>
  );

  if (accordion) {
    return (
      <Accordion defaultExpanded={defaultExpanded} disableGutters elevation={0} sx={sx.accordion}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sx.accordionSummary}>
          <ReceiptLongOutlinedIcon sx={sx.accordionSummaryIcon} aria-hidden />
          <Typography component="span" fontWeight="bold" variant="subtitle1" sx={sx.accordionTitle}>
            Mis movimientos
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={sx.accordionDetails}>
          {listContent}
        </AccordionDetails>
      </Accordion>
    );
  }

  return (
    <AlertDialog
      button={(
        <Button variant="text" color="primary" sx={sx.buttonDialog}>
          <Typography variant="caption" color="primary" fontWeight="bold" style={{ marginTop: 2 }}>Ver movimientos</Typography>
        </Button>
    )}
      hideLogo
      hideSubmitButton
      dialogTitle={(
        <Box sx={sx.panelHeaderRow} style={{ paddingInline: 17.5 }}>
          <ReceiptLongOutlinedIcon sx={sx.panelHeaderIcon} aria-hidden />
          <Typography variant="h5" fontWeight="bold" sx={sx.panelTitle}>
            Mis movimientos
          </Typography>
        </Box>
      )}
      dialogContent={(
        <Box sx={sx.panelContent}>
          {listContent}
        </Box>
      )}
      dialogContentStyle={{ paddingInline: 15, paddingBottom: 10 }}
      pageCall={{
        name: 'VER MOVIMIENTOS BNPL',
        category: 'PU - BNPL',
      }}
    />
  );
}

BnplMovements.defaultProps = {
  accordion: false,
  defaultExpanded: true,
};

export default BnplMovements;
